import React, { useState } from "react";
import { View, Text, Pressable, ScrollView } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Image } from "expo-image";
import Images from "@/assets/images";
import Icons from "@/assets/icons";

interface BrandItemProps {
  image: any;
  selected: boolean;
  onPress: () => void;
  hasIconBg?: boolean;
  isWide?: boolean;
}

const BrandItem = ({ image, selected, onPress, hasIconBg, isWide }: BrandItemProps) => (
  <Pressable
    style={[styles.brandItem, selected && styles.brandItemSelected]}
    onPress={onPress}
  >
    <View style={[styles.brandImageContainer, selected && styles.brandImageContainerSelected]}>
      <Image
        source={image}
        style={[
          styles.brandImage,
          hasIconBg && styles.brandImageWithBg,
          isWide && styles.brandImageWide,
        ]}
        contentFit="contain"
      />
    </View>
    {selected && (
      <View style={styles.checkmark}>
        <Icons.Check width={14} height={14} />
      </View>
    )}
  </Pressable>
);

interface OnboardSlide3Props {
  onSelectionChange?: (selected: string[]) => void;
}

const OnboardSlide3 = ({ onSelectionChange }: OnboardSlide3Props) => {
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  // Hardcoded brands matching Figma design
  const brands = [
    { id: "tmobile", image: Images.brandTMobile, hasIconBg: true },
    { id: "juul", image: Images.brandJuul },
    { id: "apple", image: Images.brandApple },
    { id: "walgreens", image: Images.brandWalgreens, isWide: true },
    { id: "bumble", image: Images.brandBumble, isWide: true },
    { id: "instagram", image: Images.brandInstagram },
    { id: "visa", image: Images.brandVisa, isWide: true },
    { id: "mastercard", image: Images.brandMastercard, isWide: true },
  ];

  const toggleBrand = (brandId: string) => {
    const newSelected = selectedBrands.includes(brandId)
      ? selectedBrands.filter((id) => id !== brandId)
      : [...selectedBrands, brandId];
    setSelectedBrands(newSelected);
    onSelectionChange?.(newSelected);
  };

  return (
    <View style={styles.container}>
      <View style={styles.textContainer}>
        <Text style={styles.title}>
          Which of these brands have you purchased from?
        </Text>
        <Text style={styles.subtitle}>
          Pick the ones you haven't claimed money from yet
        </Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.grid}
        showsVerticalScrollIndicator={false}
      >
        {brands.map((brand) => (
          <BrandItem
            key={brand.id}
            image={brand.image}
            selected={selectedBrands.includes(brand.id)}
            onPress={() => toggleBrand(brand.id)}
            hasIconBg={brand.hasIconBg}
            isWide={brand.isWide}
          />
        ))}
      </ScrollView>
    </View>
  );
};

export default OnboardSlide3;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 0,
    paddingTop: 12,
  },
  textContainer: {
    gap: 16,
    marginBottom: 16,
  },
  scrollView: {
    flex: 1,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    textAlign: "left",
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 24,
    color: "#161D14",
    textAlign: "left",
    opacity: 0.7,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 10,
    paddingBottom: 30,
    paddingTop: 10,
  },
  brandItem: {
    width: "48%",
    height: 110,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(15, 145, 41, 0.2)",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  brandItemSelected: {
    borderWidth: 2,
    borderColor: "rgba(15, 145, 41, 0.2)",
  },
  brandImageContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  brandImageContainerSelected: {
    opacity: 0.3,
  },
  brandImage: {
    width: 56,
    height: 56,
  },
  brandImageWithBg: {
    borderRadius: 10,
  },
  brandImageWide: {
    width: 120,
    height: 40,
  },
  checkmark: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#C6F4A8",
    justifyContent: "center",
    alignItems: "center",
  },
});
